import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Divider
} from '@mui/material';
import {
  LocationOn,
  Email,
  Phone,
  Language,
  Schedule,
  Directions
} from '@mui/icons-material';

interface Shelter {
  id: string;
  name: string;
  city: string;
  county: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  description?: string;
}

interface ShelterContactProps {
  shelter: Shelter;
}

const ShelterContact: React.FC<ShelterContactProps> = ({ shelter }) => {
  const handleContact = (type: 'email' | 'phone' | 'website') => {
    switch (type) {
      case 'email':
        if (shelter.email) {
          window.open(`mailto:${shelter.email}`, '_blank');
        }
        break;
      case 'phone':
        if (shelter.phone) {
          window.open(`tel:${shelter.phone}`, '_blank');
        }
        break;
      case 'website':
        if (shelter.website) {
          window.open(shelter.website, '_blank');
        }
        break;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Contactează {shelter.name}
      </Typography>
      
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        {/* Informații de contact */}
        <Box flex={1}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informații de contact
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {shelter.email && (
                <Box display="flex" alignItems="center" gap={2}>
                  <Email color="action" />
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {shelter.email}
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleContact('email')}
                  >
                    Trimite email
                  </Button>
                </Box>
              )}
              
              {shelter.phone && (
                <Box display="flex" alignItems="center" gap={2}>
                  <Phone color="action" />
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary">
                      Telefon
                    </Typography>
                    <Typography variant="body1">
                      {shelter.phone}
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleContact('phone')}
                  >
                    Sună
                  </Button>
                </Box>
              )}
              
              {shelter.website && (
                <Box display="flex" alignItems="center" gap={2}>
                  <Language color="action" />
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary">
                      Website
                    </Typography>
                    <Typography variant="body1">
                      {shelter.website}
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleContact('website')}
                  >
                    Vizitează
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Locație */}
        <Box flex={1}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Locație
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <LocationOn color="action" />
                <Box>
                  <Typography variant="body1">
                    {shelter.city}, {shelter.county}
                  </Typography>
                  {shelter.address && (
                    <Typography variant="body2" color="text.secondary">
                      {shelter.address}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Button 
                variant="contained" 
                startIcon={<Directions />}
                onClick={() => {
                  // TODO: Open maps with shelter location
                  console.log('Open maps for:', shelter.city, shelter.county);
                }}
                sx={{ alignSelf: 'flex-start' }}
              >
                Vezi pe hartă
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Program */}
      <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Program de vizitare
        </Typography>
        
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Schedule color="action" />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Luni - Vineri
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  09:00 - 17:00
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Schedule color="action" />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Sâmbătă
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  09:00 - 14:00
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" color="text.secondary">
          <strong>Notă:</strong> Programul poate varia în funcție de sezon și evenimente speciale. 
          Te rugăm să contactezi adapostul înainte de vizită pentru a confirma disponibilitatea.
        </Typography>
      </Paper>

      {/* Informații suplimentare */}
      {shelter.description && (
        <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Despre {shelter.name}
          </Typography>
          <Typography variant="body1">
            {shelter.description}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ShelterContact;
